import React, { useState } from "react";
import "./NewEmployee.css";
import { Input, Button, Select, Upload } from "antd";
import apiClient from "../utils/apiClient";

const { Option } = Select;

const initialDepartmentCodes = {
  "Production Support": "PS",
  "Help Desk": "HD",
  "MSBI": "MB",
  "MSSQL DBA": "DB",
  "Development": "DV",
  "HR":"HR",
  "Project Management": "PM",
  "Cloud Support": "CS",
  "Webinar": "WB",
  "Other":"OT",
  "DevOps":"DO",
  "Database Architect": "DA"
};

const NewEmployeeForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    secondary_phone: "",
    emergency_contact: "",
    gender: "",
    joining_date: "",
    dob: "",
    blood_group: "",
    address: "",
    designation: "",
    department: "",
    work_type: "",
    working_mode: "",
    report_to: "",
    role_type: "",
    week_off: [],
    password: "",
    confirmPassword: "",
    probation_end_date: "",
    profile_pic: null,
  });

  const departmentCodes = initialDepartmentCodes;
  const [generatedID, setGeneratedID] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  const restrictedDomains = ["gmail.in", "sparecode.in", "yahoo.in", "gmail.com"];

  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );

  const maxDOB = eighteenYearsAgo.toISOString().split("T")[0];
  const maxToday = today.toISOString().split("T")[0];

  /* ================= HANDLERS ================= */

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    setErrors((prev) => ({ ...prev, [name]: undefined }));

        if (name === "email") {
      const domain = value.split("@")[1];
      if (domain && !restrictedDomains.includes(domain)) {
        setErrors((prev) => ({
          ...prev,
          email: [
            "Allowed domains: gmail.in, sparecode.in, yahoo.in, gmail.com",
          ],
        }));
      }
    }

    if (["phone", "secondary_phone", "emergency_contact"].includes(name)) {
      if (!/^\d*$/.test(value)) {
        setErrors((prev) => ({ ...prev, [name]: ["Only digits allowed"] }));
        return;
      }
      if (value.length > 10) {
        setErrors((prev) => ({ ...prev, [name]: ["Must be 10 digits"] }));
        return;
      }
    }

    if (type === "file" && files?.length) {
      const file = files[0];
      setForm((prev) => ({ ...prev, profilePic: file }));
      setPreviewImage(URL.createObjectURL(file));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleMultiSelectChange = (value) => {
    if (value.length <= 2) {
      setForm((prev) => ({ ...prev, week_off: value }));
      setErrors((prev) => ({ ...prev, week_off: undefined }));
    } else {
      alert("You can only select up to 2 week off days.");
    }
  };

  /* ================= VALIDATION ================= */

const validateForm = () => {
  const newErrors = {};

  Object.keys(form).forEach((key) => {
    if (
      ["profile_pic", "confirmPassword", "probation_end_date"].includes(key)
    )
      return;

    if (!form[key] || (Array.isArray(form[key]) && form[key].length === 0)) {
      newErrors[key] = ["This field is required"];
    }
  });

  if (form.password !== form.confirmPassword) {
    newErrors.confirmPassword = ["Passwords do not match"];
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


  /* ================= SUBMIT ================= */

  const handleSubmit = async () => {
  if (!validateForm()) return;

  setLoading(true);
  setErrors({});

  try {
    const formData = new FormData();

 Object.entries(form).forEach(([key, value]) => {
  if (key === "week_off") {
    formData.append("week_off", JSON.stringify(value));
  } 
  else if (key === "profile_pic") {
    if (value instanceof File) {
      formData.append("profile_pic", value);
    }
  } 
  else if (key === "confirmPassword") {
    // skip
  } 
  else {
    formData.append(key, value);
  }
});



    // ✅ CORRECT apiClient usage
    const data = await apiClient("/auth/employees/", {
      method: "POST",
      body: formData,
      headers: {}, // DO NOT set Content-Type manually
    });

    setGeneratedID(data.employee_id);

    setTimeout(() => {
      window.location.reload();
    }, 1500);
  } catch (error) {
    console.error(error);

    // backend validation errors
    if (error && typeof error === "object") {
      setErrors(error);
    } else {
      alert("Something went wrong while submitting the form.");
    }
  } finally {
    setLoading(false);
  }
};


  const renderError = (field) =>
    errors[field] &&
    Array.isArray(errors[field]) &&
    errors[field].map((msg, idx) => (
      <p key={idx} className="error-text">
        {msg}
      </p>
    ));

  /* ================= UI ================= */

 return (
    <div className="registration-container">
      <h2>New Employee Form</h2>

      <form className="registration-form">
        <div className="form-grid">

          {/* Email */}
          <label>
            Email:
            <Input name="email" value={form.email} onChange={handleChange} />
            {renderError("email")}
          </label>

          <label>
            Employee ID:
            <Input value={generatedID} readOnly />
          </label>

          <label>
            Name:
            <Input name="name" value={form.name} onChange={handleChange} />
            {renderError("name")}
          </label>

          <label>
            Phone:
            <Input name="phone" value={form.phone} onChange={handleChange} />
            {renderError("phone")}
          </label>

          <label>
            Secondary Phone:
            <Input
              name="secondary_phone"
              value={form.secondary_phone}
              onChange={handleChange}
            />
            {renderError("secondary_phone")}
          </label>

          <label>
            Emergency Contact:
            <Input
              name="emergency_contact"
              value={form.emergency_contact}
              onChange={handleChange}
            />
            {renderError("emergency_contact")}
          </label>

          <label>
            Date of Birth:
            <input
              type="date"
              name="dob"
              value={form.dob}
              onChange={handleChange}
              max={maxDOB}
            />
            {renderError("dob")}
          </label>

          <label>
            Joining Date:
            <input
              type="date"
              name="joining_date"
              value={form.joining_date}
              onChange={handleChange}
              max={maxToday}
            />
            {renderError("joining_date")}
          </label>

          <label>
            Probation End Date:
            <input
              type="date"
              name="probation_end_date"
              value={form.probation_end_date}
              onChange={handleChange}
              min={form.joining_date || undefined}
            />
            {renderError("probation_end_date")}
          </label>

          <label>
            Blood Group:
            <Select
              value={form.blood_group}
              onChange={(val) => handleSelectChange("blood_group", val)}
              style={{ width: "100%" }}
            >
              <Option value="">Select</Option>
              {["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map((bg) => (
                <Option key={bg} value={bg}>
                  {bg}
                </Option>
              ))}
            </Select>
            {renderError("blood_group")}
          </label>

          <label>
            Gender:
            <Select
              value={form.gender}
              onChange={(val) => handleSelectChange("gender", val)}
              style={{ width: "100%" }}
            >
              <Option value="">Select</Option>
              <Option value="Male">Male</Option>
              <Option value="Female">Female</Option>
              <Option value="Other">Other</Option>
            </Select>
            {renderError("gender")}
          </label>

          <label>
            Work Type:
            <Select
              value={form.work_type}
              onChange={(val) => handleSelectChange("work_type", val)}
              style={{ width: "100%" }}
            >
              <Option value="">Select</Option>
              <Option value="Full Time">Full Time</Option>
              <Option value="Part Time">Part Time</Option>
            </Select>
            {renderError("work_type")}
          </label>

          <label>
            Working Mode:
            <Select
              value={form.working_mode}
              onChange={(val) => handleSelectChange("working_mode", val)}
              style={{ width: "100%" }}
            >
              <Option value="">Select</Option>
              <Option value="Remote">Remote</Option>
              <Option value="In Office">In Office</Option>
            </Select>
            {renderError("working_mode")}
          </label>

          <label>
            Department:
            <Select
              value={form.department}
              onChange={(val) => handleSelectChange("department", val)}
              style={{ width: "100%" }}
            >
              <Option value="">Select</Option>
              {Object.keys(departmentCodes).map((dept) => (
                <Option key={dept} value={dept}>
                  {dept}
                </Option>
              ))}
            </Select>
            {renderError("department")}
          </label>

          <label>
            Role Type:
            <Select
              value={form.role_type}
              onChange={(val) => handleSelectChange("role_type", val)}
              style={{ width: "100%" }}
            >
              <Option value="">Select</Option>
              <Option value="employee">Employee</Option>
              <Option value="manager">Manager</Option>
              <Option value="admin">Admin</Option>
            </Select>
            {renderError("role_type")}
          </label>

          <label>
            Designation:
            <Input
              name="designation"
              value={form.designation}
              onChange={handleChange}
            />
            {renderError("designation")}
          </label>

          <label>
            Report To:
            <Input
              name="report_to"
              value={form.report_to}
              onChange={handleChange}
            />
            {renderError("report_to")}
          </label>

          <label>
            Password:
            <Input.Password
              name="password"
              value={form.password}
              onChange={handleChange}
            />
            {renderError("password")}
          </label>

          <label>
            Confirm Password:
            <Input.Password
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
            />
            {renderError("confirmPassword")}
          </label>

          <label>
            Week Off:
            <Select
              mode="multiple"
              value={form.week_off}
              onChange={handleMultiSelectChange}
              style={{ width: "100%" }}
              maxTagCount={2}
              maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
            >
              {[
                "Sunday",
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday",
                "Saturday",
              ].map((day) => (
                <Option key={day} value={day}>
                  {day}
                </Option>
              ))}
            </Select>
            {renderError("week_off")}
          </label>

          <label>
            Address:
            <Input.TextArea
              name="address"
              value={form.address}
              onChange={handleChange}
              rows={3}
              placeholder="Enter full address"
            />
            {renderError("address")}
          </label>

          <label>
            Profile Picture (Optional):
            <Upload
              beforeUpload={() => false}
              onChange={({ file }) => {
                const fileObj = file.originFileObj;
                setForm((prev) => ({ ...prev, profile_pic: fileObj }));

                if (fileObj) {
                  setPreviewImage(URL.createObjectURL(fileObj));
                }
              }}
              showUploadList={false}
            >
              <Button>Upload Photo</Button>
            </Upload>

            {previewImage && (
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "cover",
                  marginTop: 10,
                  borderRadius: 8,
                }}
              />
            )}
          </label>
        </div>

        <div className="form-actions">
          <Button loading={loading} onClick={handleSubmit}>
            Create
          </Button>
          <Button>Cancel</Button>
        </div>
      </form>
    </div>
  );
};

export default NewEmployeeForm;
